import { AxiosResponse } from "axios";
import { normalize, schema } from 'normalizr';
import { createAsyncThunk } from "@reduxjs/toolkit";
import mineCraftApi from 'src/api/mineCraftApi';
import minecraftUrls from "src/configs/mine_craft/minecraftUrls";
import { BannerInterface } from 'src/store/mine_craft/bannersSlice';
import { usersEntity } from "./usersActions";
import { UserInterface } from "../../store/mine_craft/usersSlice";

const processStrategy = (value: any) => {
  return {
    ...value,
    image_url: minecraftUrls.remoteUrl(value.image_url),
  };
}

export const bannersEntity = new schema.Entity(
  'banners',
  {
    user: usersEntity
  },
  {
    processStrategy
  }
);

export const bannerEntity = new schema.Entity(
  'banner',
  {
    user: usersEntity
  },
  {
    processStrategy
  }
);

const fetchBannersCollection = async (url: string, params: object = {}) => {
  const response    = await mineCraftApi.get(url, { params })
  const normalized  = normalize<
    any,
    {
      users: { [key: string]: UserInterface };
      banners: { [key: string]: BannerInterface };
    }
  >(response.data.banners, [bannersEntity]);

  return {
    users: normalized.entities.users || {},
    banners: normalized.entities?.banners || {},
    ids: normalized.result,
    count: response.data.meta?.total_count
  };
}

const normalizeInstance = (response: AxiosResponse) => {
  const normalized = normalize<
    any,
    {
      user: { [key: string]: UserInterface };
      banner: BannerInterface;
    }
  >(response.data.banner, bannerEntity);
  return normalized.entities;
}

const normalizeUploadInstance = (response: any) => {
  const normalized = normalize<
    any,
    {
      banner: BannerInterface
    }
  >(response.banner, bannerEntity);
  return normalized.entities;
}

export const fetchBanners = createAsyncThunk(
  'banners/fetchBanners',
  async (params: object = {}) => {
    return fetchBannersCollection('/onesky/banners', params);
  }
);

export const fetchBanner = createAsyncThunk(
  'banners/fetchBanner',
  async (id: number | string) => {
    const response = await mineCraftApi.get(`/onesky/banners/${id}`);
    return normalizeInstance(response);
  }
);

export const uploadBanner = createAsyncThunk(
  'banners/createBanner',
  async (response: object, { rejectWithValue }) => {
    try {
      return normalizeUploadInstance(response);
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error);
    }
  }
);

export const createBanner = createAsyncThunk(
  'banners/createBanner',
  async (data: object, { rejectWithValue }) => {
    try {
      const response = await mineCraftApi.post(`/onesky/banners`, data);
      return normalizeInstance(response);
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error);
    }
  }
);

export const updateBanner = createAsyncThunk(
  'banners/updateBanner',
  async ({ id, data }: { id: number, data: object }, { rejectWithValue }) => {
    try {
      const response = await mineCraftApi.patch(`/onesky/banners/${id}`, data);
      return normalizeInstance(response);
    } catch (error: any) {
      return rejectWithValue(error.response.data || error)
    }
  }
);

export const destroyBanner = createAsyncThunk(
  'banners/destroyBanner',
  async (id: number) => {
    const response = await mineCraftApi.delete(`/onesky/banners/${id}`);
    return response.data;
  }
);

export const reorderBanners = createAsyncThunk(
  'banners/reorderBanners',
  async ({ ids }: { ids: any }, { rejectWithValue }) => {
    try {
      await mineCraftApi.patch('/onesky/banners/reorder', { ids })

      return { ids };
    } catch (error: any) {
      return rejectWithValue(error.response.data || error)
    }
  }
);
